import type { ContextUpdate } from '@proj-airi/server-sdk'
import type { Tool } from '@xsai/shared-chat'

import type {
  SparkNotifyTracingHooks,
  SparkTraceEvent,
} from './types'

import { errorMessageFrom } from '@moeru/std'
import { rawTool } from '@xsai/tool'
import { toJsonSchema, validate } from 'xsschema'
import { z } from 'zod'

import {
  normalizeNullableAnyOf,
  sparkNotifyCommandSchema,
} from './schema'

/**
 * Normalized `builtIn_sparkCommand` payload forwarded to downstream orchestrators.
 */
export interface SparkNotifyCommandDraft {
  /** Target agent or lane identifiers that should receive the emitted command. */
  destinations: string[]
  /** Optional interrupt mode used by downstream schedulers. */
  interrupt?: 'force' | 'soft' | boolean
  /** Optional command priority used by downstream schedulers. */
  priority?: 'critical' | 'high' | 'normal' | 'low'
  /** Optional intent describing why the downstream agent should process the command. */
  intent?: 'plan' | 'proposal' | 'action' | 'pause' | 'resume' | 'reroute' | 'context'
  /** Optional acknowledgement text that may be surfaced by the downstream consumer. */
  ack?: string
  /** Optional structured guidance generated by the notify agent for the downstream command target. */
  guidance?: {
    /** Guidance mode describing how the downstream agent should interpret the options. */
    type: 'proposal' | 'instruction' | 'memory-recall'
    /** Trait-strength map that hints at which persona qualities the downstream agent should favor. */
    persona?: Record<string, 'very-high' | 'high' | 'medium' | 'low' | 'very-low'>
    /** Candidate options the downstream agent may choose from while executing the command. */
    options: Array<{
      /** Human-readable label for the candidate option. */
      label: string
      /** Ordered steps the downstream agent should follow for the option. */
      steps: string[]
      /** Optional rationale explaining why this option is suggested. */
      rationale?: string
      /** Optional possible outcomes the downstream agent should anticipate. */
      possibleOutcome?: string[]
      /** Optional qualitative risk level for the option. */
      risk?: 'high' | 'medium' | 'low' | 'none'
      /** Optional fallback steps the downstream agent may use if the option fails. */
      fallback?: string[]
      /** Optional trigger cues indicating when the option should be selected. */
      triggers?: string[]
    }>
  }
  /** Optional context patches that should accompany the emitted command. */
  contexts?: ContextUpdate<Record<string, unknown>, undefined>[]
}

/**
 * Tool registration options for the spark-notify runtime.
 */
export interface CreateSparkNotifyToolsOptions {
  /** Receives validated command drafts emitted by `builtIn_sparkCommand`. */
  onCommands: (commands: SparkNotifyCommandDraft[]) => void
  /** Receives the no-response signal emitted by `builtIn_sparkNoResponse`. */
  onNoResponse: () => void
  /** Receives trace events describing tool exposure and execution. */
  onTrace?: SparkNotifyTracingHooks['onTrace']
  /** Enables or disables registration of the `builtIn_sparkNoResponse` tool. */
  allowNoResponse?: boolean
  /** Enables or disables registration of the `builtIn_sparkCommand` tool. */
  allowSparkCommand?: boolean
}

/**
 * Normalizes provider-facing notify command payloads into websocket draft commands.
 *
 * Use when:
 * - LLM output has been validated against `sparkNotifyCommandSchema`
 * - You need `spark:command`-compatible draft objects
 *
 * Expects:
 * - Input shape from one `commands[]` entry
 *
 * Returns:
 * - Runtime-ready command draft for downstream emitters
 */
function normalizeSparkNotifyCommand(
  command: z.infer<typeof sparkNotifyCommandSchema>['commands'][number],
): SparkNotifyCommandDraft {
  return {
    destinations: command.destinations,
    guidance: command.guidance
      ? {
          type: command.guidance.type,
          persona: command.guidance.persona?.reduce((acc, curr) => {
            acc[curr.traits] = curr.strength
            return acc
          }, {} as Record<string, 'very-high' | 'high' | 'medium' | 'low' | 'very-low'>) || undefined,
          options: command.guidance.options.map(option => ({
            ...option,
            rationale: option.rationale ?? undefined,
            possibleOutcome: option.possibleOutcome?.length ? option.possibleOutcome : undefined,
            risk: option.risk ?? undefined,
            fallback: option.fallback?.length ? option.fallback : undefined,
            triggers: option.triggers?.length ? option.triggers : undefined,
          })),
        }
      : undefined,
    // TODO: contexts can be added later
    contexts: [],
    priority: command.priority || 'normal',
    intent: command.intent || 'action',
    ack: command.ack || undefined,
    interrupt: command.interrupt === 'false' || command.interrupt == null ? false : command.interrupt,
  }
}

/**
 * Creates built-in tools used by the Spark Notify agent.
 *
 * Use when:
 * - Running the Spark Notify agent on any runtime (web, desktop, eval harness)
 * - You need "no response" and "command draft" tool pathways
 *
 * Expects:
 * - Callbacks for command collection and no-response signaling
 *
 * Returns:
 * - Tool array consumable by `@xsai/stream-text`
 */
export async function createSparkNotifyTools(options: CreateSparkNotifyToolsOptions) {
  const allowNoResponse = options.allowNoResponse !== false
  const allowSparkCommand = options.allowSparkCommand !== false

  const tools: Tool[] = []

  const sparkNoResponseTool = rawTool({
    name: 'builtIn_sparkNoResponse',
    description: 'Indicate that no response or action is needed for the current spark:notify event.',
    parameters: normalizeNullableAnyOf(await toJsonSchema(z.object({}).strict()) as any),
    execute: async (_rawPayload, context) => {
      options.onTrace?.({
        type: 'model-output-tool-call',
        payload: {
          toolName: 'builtIn_sparkNoResponse',
          toolCallId: context?.toolCallId,
        },
      } satisfies SparkTraceEvent)
      options.onNoResponse()
      options.onTrace?.({
        type: 'tool-execution',
        payload: {
          toolName: 'builtIn_sparkNoResponse',
          toolCallId: context?.toolCallId,
          responseMode: 'no-response',
        },
      } satisfies SparkTraceEvent)
      return 'AIRI System: Acknowledged, no response or action will be processed.'
    },
  })
  if (allowNoResponse)
    tools.push(sparkNoResponseTool)

  const sparkCommandTool = rawTool({
    name: 'builtIn_sparkCommand',
    description: 'Issue a spark:command to sub-agents. You can call this tool multiple times.',
    parameters: normalizeNullableAnyOf(await toJsonSchema(sparkNotifyCommandSchema) as any),
    execute: async (rawPayload, context) => {
      options.onTrace?.({
        type: 'model-output-tool-call',
        payload: {
          toolName: 'builtIn_sparkCommand',
          toolCallId: context?.toolCallId,
          rawPayload,
        },
      } satisfies SparkTraceEvent)

      try {
        const payload = rawPayload as z.infer<typeof sparkNotifyCommandSchema>
        const validated = await validate(sparkNotifyCommandSchema, payload)
        options.onCommands(validated.commands.map(normalizeSparkNotifyCommand))
        options.onTrace?.({
          type: 'tool-execution',
          payload: {
            toolName: 'builtIn_sparkCommand',
            toolCallId: context?.toolCallId,
            commandCount: validated.commands.length,
          },
        } satisfies SparkTraceEvent)
      }
      catch (error) {
        options.onTrace?.({
          type: 'tool-execution',
          payload: {
            toolName: 'builtIn_sparkCommand',
            toolCallId: context?.toolCallId,
            ok: false,
            error: errorMessageFrom(error),
          },
        } satisfies SparkTraceEvent)
        return `AIRI System: Error - invalid spark_command parameters: ${errorMessageFrom(error)}`
      }

      return 'AIRI System: Acknowledged, command fired.'
    },
  })
  if (allowSparkCommand)
    tools.push(sparkCommandTool)

  return {
    tools,
  }

  if (allowSparkCommand) {
    tools.push(rawTool({
      name: 'builtIn_sparkCommand',
      description: 'Issue a spark:command to sub-agents. You can call this tool multiple times.',
      parameters: normalizeNullableAnyOf(await toJsonSchema(sparkNotifyCommandSchema) as any),
      execute: async (rawPayload) => {
        try {
          const payload = rawPayload as z.infer<typeof sparkNotifyCommandSchema>
          const validated = await validate(sparkNotifyCommandSchema, payload)
          options.onCommands(validated.commands.map(normalizeSparkNotifyCommand))
        }
        catch (error) {
          return `AIRI System: Error - invalid spark_command parameters: ${errorMessageFrom(error)}`
        }

        return 'AIRI System: Acknowledged, command fired.'
      },
    }))
  }

  return { tools }
}
